// =============================================================================
// BeforeYouGo - the end-of-day sweep card (replaces EndOfDayDigest).
//
// From 3 PM (practice-local browser time) every staff role sees what is still
// open for them, with one-tap actions so the day can actually be closed:
//
//   my_tasks          your tasks due today or overdue   -> Done / Tomorrow
//   unclosed_visits   started, no check-in (desk, mgr)  -> No-show / Schedule
//   tomorrow_confirm  at risk, not confirmed (desk)     -> Call / Confirmed
//   copays, in_clinic, unsigned_notes, provider_items,
//   billing_holds, stuck, cm_due                        -> link to the screen
//
// Data: eod_sweep() (SECURITY INVOKER, staff RLS), read through the shared
// shell cache (lib/shellCounts.js) so the Layout's end-of-day nudge and this
// card use the same answer. Sections with nothing in them are left out.
//
// Round 4 #9 (Oct 2026): the card renders only when it has something to say
// (open items, or an error with Retry). The clear state ("You are all wrapped
// up") used to take a full card at the top of the Front Desk dashboard and
// CM Today for no reason; it now renders nothing, as briefing mode already
// did. "From 3 PM" is 3 PM in the practice's time zone, not the device's.
//
// Preview before 3 PM with ?eod=1 in the URL. "Hide for today" is a per-device
// convenience (localStorage, wrapped in try/catch).
//
// Briefing mode (Oct 2026, staff Dashboard): with `briefing` set the card
// reports its state through onStatus and renders nothing while it is off,
// loading or clear, so the Dashboard can say "all clear" once in its briefing
// hero instead of stacking this card's clear line next to two others. With
// work (or an error) it renders exactly as before. `hidden` / `onHide` let the
// hero own "Hide for today"; `refreshKey` re-reads when the hero's Refresh is
// pressed. Other dashboards pass none of these and are unchanged.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { C } from "../lib/tokens";
import { useAuth } from "../auth/AuthProvider";
import { fetchEodSweep, zonedClock } from "../lib/shellCounts";
import { Badge, Btn, Card, ErrorBanner } from "./ui";
import { ReasonChips } from "./worklist/WorklistBits";
import { useToast } from "./ToastProvider";
import { updateTask } from "../lib/tasksApi";
import { snoozeAt } from "../views/tasks/taskUtils";
import { confirmAppointment, markNoShow, localISO } from "../lib/noShowRisk";

const TONE = { danger: "red", warn: "amber", info: "blue", neutral: "neutral" };
const HIDE_KEY = "pos-eod-hidden";

export function hiddenToday() {
  try { return window.localStorage.getItem(HIDE_KEY) === localISO(); } catch (e) { return false; }
}

// "Hide for today" (per device). Shared with the Dashboard briefing hero.
export function hideForToday() {
  try { window.localStorage.setItem(HIDE_KEY, localISO()); } catch (e) { /* per-device convenience only */ }
}

export default function BeforeYouGo({ fromHour = 15, briefing = false, onStatus = null, refreshKey = 0, hidden: hiddenProp, onHide = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const preview = useMemo(() => new URLSearchParams(location.search).get("eod") === "1", [location.search]);
  const { profile } = useAuth();
  const practiceTz = profile && profile.practices && profile.practices.timezone;
  const [hour, setHour] = useState(() => zonedClock(new Date(), practiceTz).hour);
  const [hiddenLocal, setHidden] = useState(hiddenToday);
  // Controlled when the parent passes `hidden` (the Dashboard briefing hero).
  const hidden = hiddenProp === undefined ? hiddenLocal : !!hiddenProp;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const show = (preview || hour >= fromHour) && !hidden;

  // Re-check the hour every 5 minutes so the card appears at 3 PM without a reload.
  useEffect(() => {
    setHour(zonedClock(new Date(), practiceTz).hour);
    const t = setInterval(() => setHour(zonedClock(new Date(), practiceTz).hour), 300000);
    return () => clearInterval(t);
  }, [practiceTz]);

  // maxAgeMs: the first read may reuse the Layout nudge's answer from the
  // last 30s; Refresh and the one-tap actions always ask again.
  const load = useCallback(async (maxAgeMs = 0) => {
    try {
      const d = await fetchEodSweep({ maxAgeMs });
      setError(null);
      setData(d || { sections: [] });
    } catch (e) {
      setError("Couldn't load what is still open: " + ((e && e.message) || "please retry"));
    }
  }, []);
  useEffect(() => { if (show) load(30 * 1000); }, [show, load]);
  // The briefing hero's Refresh: re-read without blanking the card.
  useEffect(() => { if (refreshKey && show) load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast({ text: msg }); await load(); }
    catch (e) { setError(e.message || "That did not work"); }
    finally { setBusy(null); }
  };

  const hide = () => {
    hideForToday();
    setHidden(true);
    if (onHide) onHide();
  };

  const sections = (data && data.sections) || [];
  const s = (data && data.summary) || {};

  // Briefing status, reported as primitives so the parent's setState settles.
  const status = !show ? "off" : error ? "work" : !data ? "pending" : sections.length === 0 ? "clear" : "work";
  const openCount = sections.reduce((n, sec) => n + (Number(sec.count) || 0), 0);
  const tomorrow = data ? (s.tomorrow || 0) : null;
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;
  useEffect(() => {
    if (statusRef.current) statusRef.current({ state: status, count: openCount, tomorrow });
  }, [status, openCount, tomorrow]);

  if (!show) return null;
  // Nothing to say (loading, or all clear): no card. The Dashboard's briefing
  // hero says "all clear" itself; other screens just keep the space.
  if (status !== "work") return null;

  const summaryBits = [];
  if (s.scheduled) summaryBits.push((s.completed || 0) + " of " + s.scheduled + " visits completed");
  if (s.no_shows) summaryBits.push(s.no_shows + " no-show" + (s.no_shows === 1 ? "" : "s"));
  if (s.queue_seen || s.queue_walkouts) {
    summaryBits.push("queue: " + (s.queue_seen || 0) + " seen" + (s.queue_avg_wait != null ? ", " + s.queue_avg_wait + "m avg wait" : "")
      + (s.queue_walkouts ? ", " + s.queue_walkouts + " walk-out" + (s.queue_walkouts === 1 ? "" : "s") : ""));
  }
  summaryBits.push("tomorrow: " + (s.tomorrow || 0) + " scheduled");

  const itemActions = (sec, it) => {
    const k = it.kind + ":" + it.id;
    if (it.kind === "task") {
      return (<>
        <Btn size="sm" variant="success" disabled={busy === k} onClick={() => act(k, () => updateTask(it.id, { status: "Completed" }), "Task completed")}>Done</Btn>
        <Btn size="sm" variant="outline" disabled={busy === k} onClick={() => act(k, () => updateTask(it.id, { snoozed_until: snoozeAt("tomorrow") }), "Moved to tomorrow")}>Tomorrow</Btn>
      </>);
    }
    if (it.kind === "visit") {
      return (<>
        <Btn size="sm" variant="outline" disabled={busy === k} onClick={() => act(k, () => markNoShow({ id: it.id, patient_id: it.patient_id }), "Marked no-show")}>No-show</Btn>
        <Btn size="sm" variant="ghost" onClick={() => navigate("/schedule")}>Schedule</Btn>
      </>);
    }
    if (it.kind === "confirm") {
      return (<>
        {it.phone && <a href={"tel:" + it.phone} style={{ fontSize: 12, fontWeight: 600, color: C.brandText, padding: "5px 10px", border: "0.5px solid " + C.borderMid, borderRadius: 8, textDecoration: "none" }}>Call</a>}
        <Btn size="sm" variant="success" disabled={busy === k} onClick={() => act(k, () => confirmAppointment({ id: it.id, patient_id: it.patient_id }), "Marked confirmed")}>Confirmed</Btn>
      </>);
    }
    return null;
  };

  return (
    <Card style={{ padding: "16px 18px", marginBottom: briefing ? 0 : 16, border: "0.5px solid " + C.borderMid }}>
      <div data-testid="before-you-go" style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary }}>Before you go</div>
          <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>{data ? summaryBits.join(" - ") : "Checking what is still open..."}</div>
        </div>
        <Btn size="sm" variant="ghost" onClick={() => load()}>{error ? "Retry" : "Refresh"}</Btn>
        <Btn size="sm" variant="ghost" onClick={hide}>Hide for today</Btn>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!data ? null : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {sections.map((sec) => (
            <div key={sec.key} data-testid={"eod-" + sec.key} style={{ padding: "10px 0", borderTop: "0.5px solid " + C.borderLight }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Badge label={String(sec.count)} variant={TONE[sec.tone] || "neutral"} size="xs" />
                <span style={{ flex: "1 1 160px", minWidth: 0, fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{sec.title}</span>
                {sec.link && <Btn size="sm" variant="outline" onClick={() => navigate(sec.link)}>{sec.link_label || "Open"}</Btn>}
              </div>
              {sec.hint && <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 3 }}>{sec.hint}</div>}
              {(sec.items || []).length > 0 && (
                <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                  {sec.items.map((it) => (
                    <div key={it.kind + it.id} data-testid="eod-item" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 0 6px 8px", borderLeft: "2px solid " + C.borderLight }}>
                      <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: C.textPrimary, overflowWrap: "anywhere" }}>{it.label}</div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 1 }}>
                          {it.sub && <span style={{ fontSize: 11, color: C.textTertiary }}>{it.sub}</span>}
                          {it.reasons && <ReasonChips reasons={it.reasons} label={false} />}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{itemActions(sec, it)}</div>
                    </div>
                  ))}
                  {sec.count > (sec.items || []).length && (
                    <div style={{ fontSize: 11, color: C.textTertiary, paddingLeft: 10 }}>+ {sec.count - sec.items.length} more</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
